import { logger, loginMetrics } from "@platform/browser-logger";
import { useCallback, useEffect, useRef } from "react";

import { revokePasswordSession } from "./session-api";
import { claimsFromSession } from "./session-claims";

import type { SignedInUser, UnsupportedRole, User } from "../lib/user";
import type { LegacyMigration } from "./legacy-session";
import type { SessionActions, SessionConfig } from "./session-actions";
import type { SessionEndReason } from "./session-rules";
import type { StoredSession } from "./session-storage";

/**
 * Revokes the refresh token a migrated session shouldn't keep. The migration itself
 * runs before the first render, so that render already sees the session.
 */
export const useMigratedTokenRevocation = (
  config: SessionConfig,
  migration: LegacyMigration,
): void => {
  useEffect(() => {
    const { session, refreshTokenToRevoke } = migration;
    if (!session || !refreshTokenToRevoke) return;

    revokePasswordSession(config.apiBaseUrl, refreshTokenToRevoke).catch(
      (error: unknown) => {
        logger.warn("Revoking a migrated session's refresh token failed", {
          error: error instanceof Error ? error.message : String(error),
          sub: claimsFromSession(session).sub,
        });
      },
    );
  }, [config, migration]);
};

/** A role the app doesn't support can't be used, so its session ends. */
export const useUnsupportedRoleLogout = (
  result: User | UnsupportedRole,
  sub: string | undefined,
  endSession: SessionActions["endSession"],
): void => {
  useEffect(() => {
    if (result.status !== "unsupportedRole") return;
    logger.warn("Ended a session with an unsupported role", {
      sub,
      role: result.role,
    });
    void endSession("unsupportedRole");
  }, [result, sub, endSession]);
};

/** Tells Datadog who the user is, once per signed-in user. */
export const useLoginMetrics = (user: SignedInUser | undefined): void => {
  const userId = user?.id;
  const displayName = user?.displayName;
  const email = user?.email;
  const role = user?.role;

  useEffect(() => {
    if (!userId || !role) return;
    loginMetrics(displayName ?? "", email ?? "", role, [role]);
  }, [userId, displayName, email, role]);
};

/**
 * Reports a session that ended in another tab. The browser's storage event removes it
 * here without this tab calling endSession, so the app hears about it this way.
 * Returns the function to call when this tab ends its own session, so that ending
 * isn't reported a second time.
 */
export const useSessionEndedElsewhere = (
  session: StoredSession | undefined,
  onSessionEnd: (reason: SessionEndReason) => void,
): (() => void) => {
  const hadSession = useRef(session !== undefined);
  const endingHere = useRef(false);

  useEffect(() => {
    const sessionEnded = hadSession.current && session === undefined;
    hadSession.current = session !== undefined;
    if (!sessionEnded) return;

    if (endingHere.current) {
      endingHere.current = false;
      return;
    }
    onSessionEnd("user");
  }, [session, onSessionEnd]);

  return useCallback(() => {
    endingHere.current = true;
  }, []);
};
